import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, LogOut } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { LanguagePicker } from '@/components/language-picker';
import { useAuth } from '../../hooks/useAuth';

// The shared dropdown module is a bare re-export of the Radix primitives, so
// the popover's own look lives here — same as the gym switcher next door.
const CONTENT_CLASS =
  'z-50 min-w-60 overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0';

const ITEM_CLASS =
  'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground';

const SEPARATOR_CLASS = '-mx-1 my-1 h-px bg-border';

/** Two letters for the avatar to fall back on when there is no picture. */
function initialsOf(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : source.slice(0, 2);

  return letters.toUpperCase();
}

/**
 * The signed-in user at the foot of the sidebar. Expanded it reads as a face
 * with a name and an email; collapsed to the rail the avatar is all that is
 * left, and it is still the same button.
 *
 * Signing out and picking a theme hang off it rather than sitting on the
 * sidebar: neither is navigation, and the footer only has room for one row.
 */
export function NavUser() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { isMobile } = useSidebar();

  const name = user?.full_name?.trim() || user?.username || '';
  const email = user?.email ?? '';

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            title={name || email}
            data-testid="nav-user"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar className="size-8 rounded-lg">
              {user?.avatar_small_url && (
                <AvatarImage src={user.avatar_small_url} alt={name || email} />
              )}
              <AvatarFallback className="rounded-lg text-xs">
                {initialsOf(name, email)}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate font-semibold">{name}</span>
              <span className="truncate text-xs text-sidebar-foreground/70">{email}</span>
            </div>
            <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className={CONTENT_CLASS}
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="size-8 rounded-lg">
                {user?.avatar_small_url && (
                  <AvatarImage src={user.avatar_small_url} alt={name || email} />
                )}
                <AvatarFallback className="rounded-lg text-xs">
                  {initialsOf(name, email)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{name}</span>
                <span className="truncate text-xs text-muted-foreground">{email}</span>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator className={SEPARATOR_CLASS} />

          {/* Not menu items on purpose: picking a theme or a language is worth
              seeing before the menu closes over it. */}
          <div className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm">
            <span>{t('theme.label')}</span>
            <ThemeSwitcher />
          </div>

          <div className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm">
            <span>{t('language.label')}</span>
            <LanguagePicker />
          </div>

          <DropdownMenuSeparator className={SEPARATOR_CLASS} />

          <DropdownMenuItem
            className={ITEM_CLASS}
            onSelect={signOut}
            data-testid="nav-user-sign-out"
          >
            <LogOut className="mr-2 size-4" aria-hidden />
            {t('gymAdmin.signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
